import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Gauge,
  Search,
  ShieldAlert,
  Wrench,
} from "lucide-react";

import {
  fetchEquipmentCodes,
  normalize,
  type CodeDomain,
  type EquipmentCode,
} from "@/faults-app/lib/db";

import { AppShell } from "@/faults-app/components/app-shell";
import { faultIcon } from "@/faults-app/components/electric-icons";

/* =========================================================
   شعارات شركات VFD
   الصور موجودة داخل:
   public/brand-logos/vfd
========================================================= */

const vfdBrandLogos: Record<string, string> = {
  ABB: "/faults-app/brand-logos/vfd/abb.png",

  "Allen-Bradley": "/faults-app/brand-logos/vfd/allen-bradley.png",
  "Allen-Bradley / Rockwell": "/faults-app/brand-logos/vfd/allen-bradley.png",

  Danfoss: "/faults-app/brand-logos/vfd/danfoss.png",

  Delta: "/faults-app/brand-logos/vfd/delta.png",

  Emerson: "/faults-app/brand-logos/vfd/emerson.png",
  "Control Techniques / Emerson": "/faults-app/brand-logos/vfd/emerson.png",
  "Control Techniques": "/faults-app/brand-logos/vfd/emerson.png",

  Fuji: "/faults-app/brand-logos/vfd/fuji-electric.png",
  "Fuji Electric": "/faults-app/brand-logos/vfd/fuji-electric.png",

  Hitachi: "/faults-app/brand-logos/vfd/hitachi.png",

  Invertek: "/faults-app/brand-logos/vfd/invertek.png",

  INVT: "/faults-app/brand-logos/vfd/invt.png",

  LS: "/faults-app/brand-logos/vfd/ls-electric.png",
  "LS Electric": "/faults-app/brand-logos/vfd/ls-electric.png",

  Mitsubishi: "/faults-app/brand-logos/vfd/mitsubishi-electric.png",
  "Mitsubishi Electric": "/faults-app/brand-logos/vfd/mitsubishi-electric.png",

  Omron: "/faults-app/brand-logos/vfd/omron.png",

  Schneider: "/faults-app/brand-logos/vfd/schneider-electric.png",
  "Schneider Electric": "/faults-app/brand-logos/vfd/schneider-electric.png",

  Siemens: "/faults-app/brand-logos/vfd/siemens.png",

  Toshiba: "/faults-app/brand-logos/vfd/toshiba.png",
  TOSHIBA: "/faults-app/brand-logos/vfd/toshiba.png",

  Vacon: "/faults-app/brand-logos/vfd/vacon.png",
  VACON: "/faults-app/brand-logos/vfd/vacon.png",

  Veichi: "/faults-app/brand-logos/vfd/veichi.png",

  WEG: "/faults-app/brand-logos/vfd/weg.png",

  Yaskawa: "/faults-app/brand-logos/vfd/yaskawa.png",
};

/* =========================================================
   شعارات شركات المكيفات
   الصور موجودة داخل:
   public/brand-logos2
========================================================= */

const acBrandLogos: Record<string, string> = {
  Gree: "/faults-app/brand-logos2/gree.png",

  "General / O General": "/faults-app/brand-logos2/general-o-general.png",

  Daikin: "/faults-app/brand-logos2/daikin.png",

  Carrier: "/faults-app/brand-logos2/carrier.png",

  AUX: "/faults-app/brand-logos2/a0ux.png",

  Midea: "/faults-app/brand-logos2/midea.png",

  LG: "/faults-app/brand-logos2/lg.png",

  Hisense: "/faults-app/brand-logos2/hisense.png",

  Haier: "/faults-app/brand-logos2/haier.png",

  TCL: "/faults-app/brand-logos2/tcl.png",

  Sharp: "/faults-app/brand-logos2/sharp.png",

  Samsung: "/faults-app/brand-logos2/samsung.png",

  Panasonic: "/faults-app/brand-logos2/panasonic.png",

  "Mitsubishi Electric": "/faults-app/brand-logos2/mitsubishi-electric.png",

  TOSOT: "/faults-app/brand-logos2/tosot.png",

  Toshiba: "/faults-app/brand-logos2/toshiba.png",
};

/* =========================================================
   دمج أسماء الشركات المكررة
========================================================= */

const brandAliases: Record<string, string> = {
  GREE: "Gree",
};

function getCanonicalBrand(brand: string) {
  return brandAliases[brand] ?? brand;
}

type Props = {
  domain: CodeDomain;
  title: string;
  subtitle: string;
  brandLabel: string;
  searchPlaceholder: string;
  initialQuery?: string;
};

export function CodeBrowser({
  domain,
  title,
  subtitle,
  brandLabel,
  searchPlaceholder,
  initialQuery = "",
}: Props) {
  const [brand, setBrand] = useState<string | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [query, setQuery] = useState(initialQuery);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: codes = [], isLoading } = useQuery({
    queryKey: ["equipment-codes", domain],
    queryFn: () => fetchEquipmentCodes(domain),
  });

  const brands = useMemo(
    () =>
      Array.from(
        new Set(
          codes
            .map((c) => getCanonicalBrand(c.brand))
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [codes],
  );

  const models = useMemo(
    () =>
      Array.from(
        new Set(
          codes
            .filter(
              (c) =>
                getCanonicalBrand(c.brand) === brand,
            )
            .map((c) => c.model)
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [codes, brand],
  );

  const normalizedQuery = normalize(query.trim());

  const filtered = codes.filter((c) => {
    if (normalizedQuery) {
      const haystack = normalize(`
        ${c.brand}
        ${c.model}
        ${c.code}
        ${c.meaning_ar}
        ${c.cause_ar}
        ${c.keywords}
      `);

      return haystack.includes(normalizedQuery);
    }

    if (!brand) return false;

    if (getCanonicalBrand(c.brand) !== brand) {
      return false;
    }

    if (model && c.model !== model) return false;

    return true;
  });

  const selectedBrandLogo = brand
    ? domain === "vfd"
      ? vfdBrandLogos[brand]
      : acBrandLogos[brand]
    : undefined;

  return (
    <AppShell>
      <main className="mx-auto w-full max-w-5xl px-4 pt-6">

        {/* رأس الصفحة */}

        <header className="rounded-3xl panel-dark p-5 shadow-card">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
              <AlertTriangle className="h-6 w-6" />
            </span>

            <div className="min-w-0">
              <h1 className="truncate text-xl font-extrabold text-white">
                {title}
              </h1>

              <p className="truncate text-sm text-primary">
                {subtitle}
              </p>
            </div>
          </div>

          {/* البحث */}

          <div className="mt-4 rounded-2xl bg-card p-3 shadow-card">
            <label
              htmlFor="code-q"
              className="mb-2 flex items-center gap-2 text-sm font-bold text-black"
            >
              <Search className="h-4 w-4" />

              ابحث برمز العطل أو الشركة
            </label>

            <input
              id="code-q"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpenId(null);
              }}
              placeholder={searchPlaceholder}
              className="w-full rounded-xl border border-input bg-background p-3 text-base text-black caret-black outline-none transition placeholder:text-black/60 focus:border-primary focus:ring-3 focus:ring-primary/25"
            />
          </div>
        </header>

        {!query && (
          <>
            {/* الشركات */}

            <section className="mt-6">
              <h2 className="mb-4 text-base font-extrabold">
                {brandLabel}
              </h2>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {brands.map((b) => {
                  const logo =
                    domain === "vfd"
                      ? vfdBrandLogos[b]
                      : acBrandLogos[b];

                  const selected = brand === b;

                  return (
                    <button
                      key={b}
                      type="button"
                      title={b}
                      onClick={() => {
                        setBrand(selected ? null : b);
                        setModel(null);
                        setOpenId(null);
                      }}
                      className={`
                        press
                        group
                        relative
                        flex
                        h-[105px]
                        w-full
                        items-center
                        justify-center
                        overflow-hidden
                        rounded-2xl
                        border
                        bg-card
                        px-3
                        py-3
                        transition
                        duration-200
                        ${
                          selected
                            ? "border-primary ring-2 ring-primary/25 shadow-glow"
                            : "border-border hover:border-primary hover:shadow-card"
                        }
                      `}
                    >
                      {logo ? (
                        <>
                          <img
                            src={logo}
                            alt={b}
                            className="h-[60px] w-full max-w-[130px] object-contain transition duration-200 group-hover:scale-105"
                            onError={(e) => {
                              e.currentTarget.style.display =
                                "none";

                              const text =
                                e.currentTarget
                                  .nextElementSibling as HTMLElement | null;

                              if (text) {
                                text.style.display = "block";
                              }
                            }}
                          />

                          <span
                            className="text-center text-sm font-extrabold"
                            style={{ display: "none" }}
                          >
                            {b}
                          </span>
                        </>
                      ) : (
                        <span className="text-center text-sm font-extrabold">
                          {b}
                        </span>
                      )}

                      {selected && (
                        <span className="absolute end-2 top-2 h-2.5 w-2.5 rounded-full bg-primary shadow-glow" />
                      )}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* الموديلات */}

            {brand && models.length > 0 && (
              <section className="mt-7">
                <div className="mb-4 flex items-center gap-3">

                  {selectedBrandLogo && (
                    <div className="flex h-14 w-28 shrink-0 items-center justify-center rounded-xl border border-border bg-card p-2">
                      <img
                        src={selectedBrandLogo}
                        alt={brand}
                        className="max-h-10 max-w-full object-contain"
                      />
                    </div>
                  )}

                  <div>
                    <h2 className="text-base font-extrabold">
                      الموديل
                    </h2>

                    <p className="text-xs font-bold text-muted-foreground">
                      {brand}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {models.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        setModel(model === m ? null : m);
                        setOpenId(null);
                      }}
                      className={`
                        press
                        rounded-xl
                        border
                        px-4
                        py-2.5
                        text-sm
                        font-bold
                        transition
                        ${
                          model === m
                            ? "border-primary bg-primary text-primary-foreground shadow-glow"
                            : "border-border bg-card hover:border-primary"
                        }
                      `}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* النتائج */}

        <section className="mt-6 space-y-3 pb-8">
          {isLoading && (
            <p className="p-6 text-center text-muted-foreground">
              جارٍ التحميل...
            </p>
          )}

          {!isLoading && !brand && !query && (
            <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              اختر الشركة المصنعة أولاً، أو اكتب رمز العطل
              مباشرة في مربع البحث.
            </p>
          )}

          {!isLoading &&
            (brand || query) &&
            filtered.length === 0 && (
              <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                لا توجد نتائج مطابقة. جرّب رمزاً آخر أو شركة
                أخرى.
              </p>
            )}

          {filtered.map((c) => (
            <CodeCard
              key={c.id}
              item={c}
              open={openId === c.id}
              onToggle={() =>
                setOpenId(
                  openId === c.id
                    ? null
                    : c.id,
                )
              }
            />
          ))}
        </section>
      </main>
    </AppShell>
  );
}

function CodeCard({
  item,
  open,
  onToggle,
}: {
  item: EquipmentCode;
  open: boolean;
  onToggle: () => void;
}) {
  const Ico = faultIcon(
    `${item.meaning_ar} ${item.keywords}`,
  );

  return (
    <article className="card-tech overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="press flex w-full items-center gap-3 p-4 text-right"
      >
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-linear-to-br from-primary to-primary-glow text-primary-foreground">
          <Ico className="h-6 w-6" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="rounded-lg bg-steel px-2 py-0.5 font-mono text-sm font-extrabold text-primary">
              {item.code}
            </span>

            <span className="text-xs font-bold text-muted-foreground">
              {getCanonicalBrand(item.brand)} — {item.model}
            </span>
          </span>

          <span className="mt-1 block text-sm font-extrabold">
            {item.meaning_ar}
          </span>
        </span>

        <ArrowLeft
          className={`h-5 w-5 shrink-0 text-primary-dark transition-transform ${
            open ? "-rotate-90" : ""
          }`}
        />
      </button>

      {open && (
        <div className="animate-fade-in space-y-4 border-t border-border p-4 text-sm">

          {item.cause_ar && (
            <Block
              title="السبب المحتمل"
              icon={
                <AlertTriangle className="h-4 w-4" />
              }
            >
              <p className="leading-relaxed">
                {item.cause_ar}
              </p>
            </Block>
          )}

          {item.steps.length > 0 && (
            <Block
              title="خطوات الفحص"
              icon={<Gauge className="h-4 w-4" />}
            >
              <ol className="space-y-2">
                {item.steps.map((s, i) => (
                  <li
                    key={i}
                    className="flex gap-2 rounded-xl border border-border bg-muted/40 p-2"
                  >
                    <span className="font-extrabold text-primary-dark">
                      {i + 1}.
                    </span>

                    <span className="min-w-0 flex-1">
                      {s}
                    </span>
                  </li>
                ))}
              </ol>
            </Block>
          )}

          {item.solutions.length > 0 && (
            <Block
              title="الحلول المقترحة"
              icon={
                <CheckCircle2 className="h-4 w-4" />
              }
            >
              <ul className="list-inside list-disc space-y-1">
                {item.solutions.map((s, i) => (
                  <li key={i}>
                    {s}
                  </li>
                ))}
              </ul>
            </Block>
          )}

          {item.parts.length > 0 && (
            <Block
              title="الأجزاء التي يجب فحصها"
              icon={<Wrench className="h-4 w-4" />}
            >
              <div className="flex flex-wrap gap-2">
                {item.parts.map((p, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-border bg-muted px-3 py-1 text-xs font-bold text-muted-foreground"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </Block>
          )}

          {item.safety_ar && (
            <p className="flex gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3 font-bold text-destructive">
              <ShieldAlert className="h-5 w-5 shrink-0" />

              <span>
                تحذير سلامة: {item.safety_ar}
              </span>
            </p>
          )}
        </div>
      )}
    </article>
  );
}

function Block({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="mb-2 flex items-center gap-2 text-sm font-extrabold text-primary-dark">
        {icon}
        {title}
      </h3>

      {children}
    </div>
  );
}